import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

interface User {
  id: number;
  name: string;
  email: string;
  is_connected: boolean;
  last_session: string | null;
  created_at: string;
  updated_at: string;
  photo: string | null;
  auth_provider: string | null;
  is_support_member?: number;
  is_sales_member?: number;
  is_stripe_testing?: number;
  // Inline data from backend (to avoid multiple queries)
  account_info?: {
    subscription_id: number | null;
    seats: number | null;
    status: string | null;
    on_hold_reason: string | null;
    on_hold_by: number | null;
    on_hold_at: string | null;
  } | null;
  seat_info?: {
    accountId: number | null;
    purchasedSeats: number;
    usedSeats: number;
    availableSeats: number;
  };
  organizations_info?: {
    count: number;
    names: string[];
  };
  credits_info?: {
    totalUsed: number;
    byMonth: Array<{ month: string; credits: number }>;
  };
  onboarding_source?: string | null;
  account_mode?: string | null;
  /** Organization vertical: "software" | "education" (null when user has no orgs) */
  industry?: string | null;
}

interface UserAccount {
  id: number;
  title: string;
  description: string;
  organization_name: string;
  created_at: string;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  newToday: number;
  newUsersThisMonth: number;
}

interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'all' | 'active' | 'inactive';
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  exclude_ghost?: number;
}

interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface PaginatedApiResponse<T> extends ApiResponse<T[]> {
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/users',
    prepareHeaders: (headers, { getState }) => {
      // Try Redux store first, then localStorage as fallback
      const state = getState() as any;
      const token = state.auth?.token || localStorage.getItem('admin_token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  // Increase cache time to reduce unnecessary refetches
  keepUnusedDataFor: 60, // Keep data in cache for 60 seconds
  tagTypes: ['User', 'UserStats'],
  endpoints: (builder) => ({
    // Get paginated users with filters
    getUsers: builder.query<PaginatedResponse<User>, UserFilters>({
      query: (filters) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params.append(key, String(value));
          }
        });
        return `/?${params.toString()}`;
      },
      transformResponse: (response: PaginatedApiResponse<User>) => {
        if (response.success) {
          const meta = response.meta ?? {
            page: 1,
            limit: response.data?.length ?? 0,
            total: response.data?.length ?? 0,
            totalPages: 1,
          };

          // Ensure is_support_member and is_sales_member are proper numbers
          const transformedData = (response.data ?? []).map(user => ({
            ...user,
            is_support_member: user.is_support_member ? Number(user.is_support_member) : 0,
            is_sales_member: user.is_sales_member ? Number(user.is_sales_member) : 0,
          }));

          return {
            data: transformedData,
            meta,
          };
        }
        throw new Error(response.message || 'Failed to fetch users');
      },
      transformErrorResponse: (response: any) => {
        return response.data?.message || response.error || 'Failed to fetch users';
      },
      providesTags: ['User'],
    }),
    
    // Get user by ID
    getUserById: builder.query<User, number>({
      query: (id) => `/${id}`,
      transformResponse: (response: ApiResponse<User>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch user');
      },
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),
    
    // Get user accounts
    getUserAccounts: builder.query<UserAccount[], number>({
      query: (id) => `/${id}/accounts`,
      transformResponse: (response: ApiResponse<UserAccount[]>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch user accounts');
      },
      providesTags: (_result, _error, id) => [{ type: 'User', id: `${id}-accounts` }],
    }),
    
    // Get user statistics
    getUserStats: builder.query<UserStats, void>({
      query: () => '/stats',
      transformResponse: (response: ApiResponse<UserStats>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch user stats');
      },
      providesTags: ['UserStats'],
    }),
    
    // Update user status
    updateUserStatus: builder.mutation<void, { id: number; isConnected: boolean }>({
      query: ({ id, isConnected }) => ({
        url: `/${id}/status`,
        method: 'PUT',
        body: { isConnected },
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to update user status');
        }
      },
      invalidatesTags: ['User'],
    }),
    
    // Delete user (soft delete)
    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to delete user');
        }
      },
      invalidatesTags: ['User'],
    }),
    
    // Search users
    searchUsers: builder.query<User[], string>({
      query: (query) => `/search?q=${encodeURIComponent(query)}`,
      transformResponse: (response: ApiResponse<User[]>) => {
        if (response.success) {
          // Ensure is_support_member and is_sales_member are proper numbers
          return (response.data ?? []).map(user => ({
            ...user,
            is_support_member: user.is_support_member ? Number(user.is_support_member) : 0,
            is_sales_member: user.is_sales_member ? Number(user.is_sales_member) : 0,
          }));
        }
        throw new Error(response.message || 'Failed to search users');
      },
      providesTags: ['User'],
    }),
    
    // Get user activity
    getUserActivity: builder.query<any[], number>({
      query: (id) => `/${id}/activity`,
      transformResponse: (response: ApiResponse<any[]>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch user activity');
      },
      providesTags: (_result, _error, id) => [{ type: 'User', id: `${id}-activity` }],
    }),
    
    // Get user analytics
    getUserAnalytics: builder.query<any, number>({
      query: (id) => `/${id}/analytics`,
      transformResponse: (response: ApiResponse<any>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch user analytics');
      },
      providesTags: (_result, _error, id) => [{ type: 'User', id: `${id}-analytics` }],
    }),

    // Get verification URL for a user
    getVerificationUrl: builder.query<{ verificationUrl: string }, number>({
      query: (id) => `/${id}/verification-url`,
      transformResponse: (response: ApiResponse<{ verificationUrl: string }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch verification URL');
      },
    }),

    // Get scope creep report verify link (same link sent by email) for simulating the flow
    getScopeReportLink: builder.query<{ url: string }, number>({
      query: (id) => `/${id}/scope-report-link`,
      transformResponse: (response: ApiResponse<{ url: string }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get scope creep report link');
      },
    }),

    // Get reshuffle report verify link (same token, reshuffle-report path – for reshuffle flow)
    getReshuffleReportLink: builder.query<{ url: string }, number>({
      query: (id) => `/${id}/reshuffle-report-link`,
      transformResponse: (response: ApiResponse<{ url: string }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get reshuffle report link');
      },
    }),

    // Get startup report verify link (same link sent by email for /startup flow)
    getStartupReportLink: builder.query<{ url: string }, number>({
      query: (id) => `/${id}/startup-report-link`,
      transformResponse: (response: ApiResponse<{ url: string }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get startup report link');
      },
    }),

    // Get pending scope report email verification (shows requested email that hasn't been verified)
    getPendingScopeReportEmail: builder.query<{ email: string; createdAt: string; token: string; flow?: 'scope' | 'plan' | 'trial' | 'reshuffle' | 'check' | 'motiocomp' } | null, number>({
      query: (id) => `/${id}/pending-scope-report-email`,
      transformResponse: (response: ApiResponse<{ email: string; createdAt: string; token: string; flow?: 'scope' | 'plan' | 'trial' | 'reshuffle' | 'check' | 'motiocomp' } | null>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get pending email verification');
      },
    }),

    // Get pending startup report email verification (shows requested email that hasn't been verified)
    getPendingStartupReportEmail: builder.query<{ email: string; createdAt: string; token: string } | null, number>({
      query: (id) => `/${id}/pending-startup-report-email`,
      transformResponse: (response: ApiResponse<{ email: string; createdAt: string; token: string } | null>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get pending startup email verification');
      },
    }),

    // Get risk intelligence report verify link (same link sent by email for /riskintelligence flow)
    getRiskIntelligenceReportLink: builder.query<{ url: string }, number>({
      query: (id) => `/${id}/riskintelligence-report-link`,
      transformResponse: (response: ApiResponse<{ url: string }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get risk intelligence report link');
      },
    }),

    // Get pending risk intelligence report email verification (shows requested email that hasn't been verified yet)
    getPendingRiskIntelligenceReportEmail: builder.query<{ email: string; createdAt: string; token: string } | null, number>({
      query: (id) => `/${id}/pending-riskintelligence-report-email`,
      transformResponse: (response: ApiResponse<{ email: string; createdAt: string; token: string } | null>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get pending risk intelligence email verification');
      },
    }),

    // Get trial resources verify link (same link sent in the resources email)
    getTrialResourcesLink: builder.query<{ url: string }, number>({
      query: (id) => `/${id}/trial-resources-link`,
      transformResponse: (response: ApiResponse<{ url: string }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get trial resources link');
      },
    }),

    // Get pending trial resources email verification (shows requested email that hasn't been verified yet)
    getPendingTrialResourcesEmail: builder.query<{ email: string; createdAt: string; token: string; flow: string } | null, number>({
      query: (id) => `/${id}/pending-trial-resources-email`,
      transformResponse: (response: ApiResponse<{ email: string; createdAt: string; token: string; flow: string } | null>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get pending trial resources email verification');
      },
    }),

    // Manually complete scope report email verification (admin can update user email without email click)
    completeScopeReportEmailVerification: builder.mutation<{ success: boolean; email: string }, number>({
      query: (id) => ({
        url: `/${id}/complete-scope-report-email`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ success: boolean; email: string }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to complete email verification');
      },
      invalidatesTags: ['User'],
    }),

    // Reset user password
    resetUserPassword: builder.mutation<{ success: boolean; wasGoogleUser?: boolean }, { id: number; password: string }>({
      query: ({ id, password }) => ({
        url: `/${id}/reset-password`,
        method: 'PUT',
        body: { password },
      }),
      transformResponse: (response: ApiResponse<{ success: boolean; wasGoogleUser?: boolean }>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to reset password');
        }
        return {
          success: true,
          wasGoogleUser: (response.data as any)?.wasGoogleUser || false
        };
      },
      invalidatesTags: ['User'],
    }),

    // Get user account info (subscription_id, seats, and hold status)
    getUserAccountInfo: builder.query<{ 
      subscription_id: number | null; 
      seats: number | null; 
      status: string | null;
      on_hold_reason: string | null;
      on_hold_by: number | null;
      on_hold_at: string | null;
    }, number>({
      query: (id) => `/${id}/account-info`,
      transformResponse: (response: ApiResponse<{ 
        subscription_id: number | null; 
        seats: number | null; 
        status: string | null;
        on_hold_reason: string | null;
        on_hold_by: number | null;
        on_hold_at: string | null;
      }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch user account info');
      },
      // Keep data longer in cache
      keepUnusedDataFor: 120, // 2 minutes
      providesTags: (_result, _error, id) => [{ type: 'User', id: `${id}-account-info` }],
    }),

    // Update user to enterprise plan
    updateUserEnterprisePlan: builder.mutation<void, { id: number; seats?: number }>({
      query: ({ id, seats }) => ({
        url: `/${id}/enterprise-plan`,
        method: 'PUT',
        body: { seats },
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to update enterprise plan');
        }
      },
      invalidatesTags: ['User'],
    }),

    // Update user account mode (OBSERVATION or EXECUTION)

    // Get user seat info (account_id, purchased seats, used seats)
    getUserSeatInfo: builder.query<{
      accountId: number | null;
      purchasedSeats: number;
      usedSeats: number;
      availableSeats: number;
      stripeCustomerId: string | null;
      stripeSubscriptionId: string | null;
    }, number>({
      query: (id) => `/${id}/seat-info`,
      transformResponse: (response: ApiResponse<{
        accountId: number | null;
        purchasedSeats: number;
        usedSeats: number;
        availableSeats: number;
        stripeCustomerId: string | null;
        stripeSubscriptionId: string | null;
      }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch user seat info');
      },
      // Keep data longer in cache
      keepUnusedDataFor: 120, // 2 minutes
      providesTags: (_result, _error, id) => [{ type: 'User', id: `${id}-seat-info` }],
    }),

    // Get user organizations (organizations owned by the user)
    getUserOrganizations: builder.query<{ count: number; names: string[] }, number>({
      query: (id) => `/${id}/organizations`,
      transformResponse: (response: ApiResponse<{ count: number; names: string[] }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch user organizations');
      },
      // Keep data longer in cache
      keepUnusedDataFor: 120, // 2 minutes
      providesTags: (_result, _error, id) => [{ type: 'User', id: `${id}-organizations` }],
    }),

    // Update user seats (add or reduce)
    updateUserSeats: builder.mutation<{
      success: boolean;
      message: string;
      purchasedSeats: number;
      usedSeats: number;
      proratedAmount?: number;
      isEnterprise?: boolean;
      invoiceId?: number;
      requiresPayment?: boolean;
    }, { 
      id: number; 
      newSeatCount: number;
      invoicePaid?: boolean;
      periodMonths?: number;
      billingType?: 'PER_SEAT' | 'CUSTOM_TOTAL';
      customAmount?: number;
    }>({
      query: ({ id, newSeatCount, invoicePaid, periodMonths, billingType, customAmount }) => ({
        url: `/${id}/seats`,
        method: 'PUT',
        body: { 
          newSeatCount,
          ...(invoicePaid !== undefined && { invoicePaid }),
          ...(periodMonths !== undefined && { periodMonths }),
          ...(billingType && { billingType }),
          ...(customAmount !== undefined && { customAmount })
        },
      }),
      transformResponse: (response: ApiResponse<{
        success: boolean;
        message: string;
        purchasedSeats: number;
        usedSeats: number;
        proratedAmount?: number;
        isEnterprise?: boolean;
        invoiceId?: number;
      }>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to update user seats');
      },
      invalidatesTags: ['User'],
    }),

    // Get enterprise invoices
    getEnterpriseInvoices: builder.query<Array<{
      id: number;
      accountId: number;
      userId: number;
      userName: string;
      userEmail: string;
      invoiceType: string;
      billingType: 'PER_SEAT' | 'CUSTOM_TOTAL';
      seatsAdded: number;
      seatsRemoved: number;
      amount: number;
      periodMonths: number | null;
      startsAt: string | null;
      expiresAt: string | null;
      status: string;
      description: string;
      paidAt: string | null;
      createdAt: string;
    }>, number | void>({
      query: (userId) => userId ? `/enterprise-invoices?userId=${userId}` : '/enterprise-invoices',
      transformResponse: (response: ApiResponse<Array<any>>) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to fetch enterprise invoices');
      },
      providesTags: ['User'],
    }),

    // Mark enterprise invoice as paid
    markEnterpriseInvoicePaid: builder.mutation<void, number>({
      query: (invoiceId) => ({
        url: `/enterprise-invoices/${invoiceId}/mark-paid`,
        method: 'PUT',
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to mark invoice as paid');
        }
      },
      invalidatesTags: ['User'],
    }),

    // Create enterprise invoice manually
    // Supports two billing types:
    // - PER_SEAT: Standard $39/seat pricing
    // - CUSTOM_TOTAL: Custom amount for a specific period (e.g., $2000 for 10 seats for 3 months)
    createEnterpriseInvoice: builder.mutation<
      { invoiceId: number; amount: number; expiresAt?: string },
      { 
        userId: number; 
        seats: number; 
        description?: string;
        billingType?: 'PER_SEAT' | 'CUSTOM_TOTAL';
        customAmount?: number;
        periodMonths?: number;
      }
    >({
      query: ({ userId, seats, description, billingType, customAmount, periodMonths }) => ({
        url: `/${userId}/enterprise-invoice`,
        method: 'POST',
        body: { seats, description, billingType, customAmount, periodMonths },
      }),
      transformResponse: (response: ApiResponse<{ invoiceId: number; amount: number; expiresAt?: string }>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to create enterprise invoice');
        }
        return response.data;
      },
      invalidatesTags: ['User'],
    }),

    // Put account on hold (Enterprise accounts only)
    putAccountOnHold: builder.mutation<void, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/${id}/hold`,
        method: 'PUT',
        body: { reason },
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to put account on hold');
        }
      },
      invalidatesTags: ['User'],
    }),

    // Remove account hold (reactivate)
    removeAccountHold: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/unhold`,
        method: 'PUT',
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to remove account hold');
        }
      },
      invalidatesTags: ['User'],
    }),

    // Switch account status (Free, Enterprise, Self-serve, On hold)
    switchAccountStatus: builder.mutation<void, { id: number; status: 'FREE' | 'ENTERPRISE' | 'SELF_SERVE' | 'ON_HOLD'; reason?: string }>({
      query: ({ id, status, reason }) => ({
        url: `/${id}/account-status`,
        method: 'PUT',
        body: { status, reason },
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to switch account status');
        }
      },
      invalidatesTags: ['User'],
    }),

    // Get Motionode Support and Sales users
    getMotionodeSupportSalesUsers: builder.query<{ support: User | null; sales: User | null }, void>({
      query: () => '/motionode-support-sales',
      transformResponse: (response: ApiResponse<{ support: User | null; sales: User | null }>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch Motionode Support/Sales users');
        }
        return response.data;
      },
      providesTags: ['User'],
    }),

    // Toggle user as Motionode Support
    toggleMotionodeSupport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/toggle-support`,
        method: 'PUT',
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to toggle Motionode Support role');
        }
      },
      invalidatesTags: ['User'],
    }),

    // Toggle user as Motionode Sales
    toggleMotionodeSales: builder.mutation<void, { id: number; welcomeMessage?: string | null }>({
      query: ({ id, welcomeMessage }) => ({
        url: `/${id}/toggle-sales`,
        method: 'PUT',
        body: { welcomeMessage },
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to toggle Motionode Sales role');
        }
      },
      invalidatesTags: ['User'],
    }),

    // Toggle user Stripe testing mode
    toggleStripeTesting: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/toggle-stripe-testing`,
        method: 'PUT',
      }),
      transformResponse: (response: ApiResponse<void>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to toggle Stripe testing mode');
        }
      },
      invalidatesTags: ['User'],
    }),

    // Generate temporary access password
    generateTemporaryAccessPassword: builder.mutation<{ password: string; expiresAt: string }, number>({
      query: (id) => ({
        url: `/${id}/temporary-access-password`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ password: string; expiresAt: string }>) => {
        if (!response.success) {
          throw new Error(response.message || 'Failed to generate temporary access password');
        }
        return response.data;
      },
      invalidatesTags: ['User'],
    }),
  }),
})

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetUserAccountsQuery,
  useGetUserStatsQuery,
  useUpdateUserStatusMutation,
  useDeleteUserMutation,
  useSearchUsersQuery,
  useGetUserActivityQuery,
  useGetUserAnalyticsQuery,
  useGetVerificationUrlQuery,
  useLazyGetScopeReportLinkQuery,
  useLazyGetReshuffleReportLinkQuery,
  useLazyGetStartupReportLinkQuery,
  useLazyGetRiskIntelligenceReportLinkQuery,
  useLazyGetTrialResourcesLinkQuery,
  useGetPendingScopeReportEmailQuery,
  useGetPendingStartupReportEmailQuery,
  useGetPendingRiskIntelligenceReportEmailQuery,
  useGetPendingTrialResourcesEmailQuery,
  useCompleteScopeReportEmailVerificationMutation,
  useResetUserPasswordMutation,
  useGetUserAccountInfoQuery,
  useUpdateUserEnterprisePlanMutation,
  useGetUserSeatInfoQuery,
  useUpdateUserSeatsMutation,
  useGetEnterpriseInvoicesQuery,
  useMarkEnterpriseInvoicePaidMutation,
  useCreateEnterpriseInvoiceMutation,
  useGetUserOrganizationsQuery,
  usePutAccountOnHoldMutation,
  useRemoveAccountHoldMutation,
  useSwitchAccountStatusMutation,
  useGetMotionodeSupportSalesUsersQuery,
  useToggleMotionodeSupportMutation,
  useToggleMotionodeSalesMutation,
  useToggleStripeTestingMutation,
  useGenerateTemporaryAccessPasswordMutation,
} = usersApi 